import React from "react"
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from "next"
import { AllProduct, getAllProducts, getAllTechnologies, ProductsColorsItem } from "../../lib/contentful/product/getAllProducts"
import { dev } from "../../lib/dev"
import { Region, regions } from "../../lib/lists/regions"
import { isArray } from "lodash"
import { getProductByEntryId, getTechnologyBySlug } from "../../lib/contentful/product/getProductBySlug"
import { Entry, EntryCollection, Tag } from "contentful"
import { TypeProductEntitySkeleton, TypeTechnologySkeleton, TypeTextBlockSkeleton } from "../../lib/contentful/ContentfulVzdelanibudoucnostiTypes"
import { cities, City } from "../../lib/lists/cities"
import { TypeCourseTypeTechnologyPageComponent } from "../../components/Pages/TypeCourseTypeTechnology"
import { webUrl } from "../../config/web"
import { NextSeo } from "next-seo"
import { Layout } from "../../components/Layout/Layout"
import { SinglePage } from "../../components/SinglePage/SinglePage"
import { css } from "@emotion/react"
import { Section } from "../../components/Section"
import { Button, Col, List, Row, Space, Spin, Typography } from "antd"
import { CoursesList } from "../../components/CoursesList/CoursesList"
import { SubscribeForm } from "../../components/SubscribeForm/SubscribeForm"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { documentToRenderOptions } from "../../components/Layout/documentToRenderOptions"
import { AuthorAndFeedbacks, getCourseBySlugLegacy, getCourseList } from "../../lib/prismic/courses"
import { extractSlugAndId } from "../../lib/contentful/product/extractSlugAndId"
import { PrismicDocument } from "@prismicio/types"
import { slugifyCourse } from "../../lib/prismic/common"
import { getProductSlug } from "../../lib/contentful/product/getProductSlug"
import { CoursesSlugPage } from "../../components/Courses/CoursesSlugPage"
import { getCourse } from "../../lib/prismic/getCourse"
import Link from "next/link"

const KrouzkyProDetiSlug: React.FC<Props> = ({ slug, technology, product, legacyEntry, regionOrCity, contentfulProducts }) => {
  if (legacyEntry) {
    return <CoursesSlugPage slug={slug} document={legacyEntry} blogPostsWithSameTag={null} openSimilarCourses={null} />
  }

  if (regionOrCity) {
    const { title, longDescription, description, keywords, seoDescription } = regionOrCity

    return (
      <TypeCourseTypeTechnologyPageComponent
        courses={contentfulProducts?.products?.items}
        productsColors={contentfulProducts?.productsColors}
        title={title}
        description={description}
        keywords={keywords}
        long_description={longDescription}
        seoDescription={seoDescription}
        url={`${webUrl}/${slug}`}
      />
    )
  }

  const url = `${webUrl}/pocitacove-krouzky-pro-deti/${slug}`

  if (technology) {
    const {
      fields: { title, description, content, color }
    } = technology

    return (
      <>
        <NextSeo
          title={title}
          description={description}
          canonical={url}
          openGraph={{
            url: url,
            title: title,
            description: description,
            images: [
              {
                url: `${webUrl}/images/header_laptop.jpg`,
                width: 1920,
                height: 1080,
                alt: "header_laptop"
              }
            ]
          }}
        />

        <Layout>
          <SinglePage color={color} title={title}>
            <section>
              <Row gutter={[40, 0]}>
                <Col
                  md={16}
                  css={css`
                    background-color: #fff;
                    padding: 20px 0 20px 0;
                  `}>
                  <article>{content ? documentToReactComponents(content, documentToRenderOptions) : null}</article>
                </Col>

                <Col
                  md={8}
                  css={css`
                    padding: 20px 0 20px 0;
                  `}>
                  <Space direction="vertical" style={{ display: "initial" }}>
                    {technology.fields?.textBlocks?.map((block: Entry<TypeTextBlockSkeleton, undefined, string>) => {
                      if (block.fields.title.length === 0 || !block.fields.content) {
                        return null
                      }

                      return (
                        <div
                          key={block.sys.id}
                          css={css`
                            margin-bottom: 20px;

                            .ant-list-sm .ant-list-item {
                              padding: 8px 0;
                            }
                          `}>
                          <Typography.Title level={4}>{block.fields.title}</Typography.Title>
                          <List
                            size="small"
                            css={css`
                              padding: 14px;
                              background-color: #fff;
                              border-radius: 20px;

                              .ant-typography {
                                &:last-of-type {
                                  margin-bottom: 0;
                                }
                              }

                              .ant-spin-container {
                                .ant-typography {
                                  &:last-of-type {
                                    margin-bottom: 0;
                                  }
                                }
                              }

                              .ant-list-item {
                                &:first-of-type {
                                  padding-top: 0;
                                }

                                &:last-of-type {
                                  padding-bottom: 0;

                                  .ant-typography {
                                    &:last-of-type {
                                      margin-bottom: 0;
                                    }
                                  }
                                }
                              }
                            `}>
                            {documentToReactComponents(block.fields.content, documentToRenderOptions)}
                          </List>
                        </div>
                      )
                    })}
                  </Space>
                </Col>
              </Row>

              <Row
                css={css`
                  margin-top: 20px;
                `}>
                <Col>
                  {contentfulProducts?.products?.items?.length > 0 ? (
                    <>
                      <Typography.Title level={2}>Kroužky &quot;{title}&quot;</Typography.Title>
                      <CoursesList
                        courses={contentfulProducts?.products?.items}
                        productsColors={contentfulProducts?.productsColors}
                        informationOnly={true}
                        css={css`
                          margin: 20px 0 0 0;
                        `}
                      />
                    </>
                  ) : null}
                </Col>
              </Row>
            </section>

            <Section>
              <Typography.Title level={2}>Odběr novinek</Typography.Title>
              <SubscribeForm tags={["krouzky", "pocitacove-krouzky-pro-deti", title]} />
            </Section>
          </SinglePage>
        </Layout>
      </>
    )
  }

  if (product) {
    const { descriptionAsText, name, content } = getCourse(product)

    return (
      <>
        <NextSeo
          title={name}
          description={descriptionAsText}
          canonical={url}
          openGraph={{
            url: url,
            title: name,
            description: descriptionAsText,
            images: [
              {
                url: `${webUrl}/images/header_laptop.jpg`,
                width: 1920,
                height: 1080,
                alt: "header_laptop"
              }
            ]
          }}
        />

        <Layout>
          <SinglePage
            title={name}
            css={css`
              background-color: #fff;
            `}>
            <Section
              css={css`
                padding: 30px 0 15px 0;
              `}>
              <article>
                {content}

                <Link href={`/registrace/${slug}`}>
                  <Button type="primary">Přejít na registraci</Button>
                </Link>
              </article>

              {contentfulProducts?.products?.items?.length > 0 ? (
                <>
                  <Typography.Title level={2}>Otevřené registrace kroužků technologie &quot;{name}&quot;</Typography.Title>
                  <CoursesList
                    courses={contentfulProducts?.products?.items}
                    productsColors={contentfulProducts?.productsColors}
                    informationOnly={true}
                    css={css`
                      margin: 20px 0 0 0;
                    `}
                  />
                </>
              ) : null}
            </Section>

            <Section>
              <Typography.Title level={2}>Odběr novinek</Typography.Title>
              <SubscribeForm tags={["krouzky", "pocitacove-krouzky-pro-deti", name]} />
            </Section>
          </SinglePage>
        </Layout>
      </>
    )
  }

  return <Spin />
}

interface Props {
  slug: string
  regionOrCity: Region | City | null
  technology: Entry<TypeTechnologySkeleton | null, undefined, string> | null
  product: Entry<TypeProductEntitySkeleton, undefined, string> | null
  legacyEntry: {
    course: PrismicDocument<Record<string, any>, string, string> | null
    parentCourses: any[]
    authorWithFeedbacks: AuthorAndFeedbacks | null
  } | null
  tags: Tag[]
  contentfulProducts: AllProduct
}

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const prismicCourses = await getCourseList()
  const contentfulCourses = await getAllProducts({ "fields.subType[in]": ["course", "sampleCourse"] })

  const pathsWithId = (prismicCourses?.results || []).map((d: PrismicDocument<any>) => {
    const { slug } = slugifyCourse(d)
    return { params: { slug } }
  })

  const contentfulPaths = (contentfulCourses?.products?.items || []).map((p) => {
    return { params: { slug: getProductSlug(p) } }
  })

  const contentfulTechnologies = await getAllTechnologies()
  const technologiesPaths = contentfulTechnologies.items.map((d) => {
    return { params: { slug: d.fields.slug } }
  })

  const regionPaths = regions.map(({ slug2 }) => {
    return { params: { slug: slug2 } }
  })

  const citiesPaths = cities.map(({ slug }) => {
    return { params: { slug } }
  })

  return {
    paths: [...technologiesPaths, ...regionPaths, ...citiesPaths, ...pathsWithId, ...contentfulPaths],
    fallback: true
  }
}

export const getStaticProps: GetStaticProps = async ({ params }): Promise<GetStaticPropsResult<Props>> => {
  const slug: string = isArray(params?.slug) ? params?.slug?.[0] : params.slug

  let regionOrCity: Region | City | null = null
  let tags: Tag[] = []

  const contentfulProducts = await getAllProducts({ "fields.subType[in]": ["course", "sampleCourse"] })

  const filteredItems = contentfulProducts?.products?.items.filter((p) => p.fields.technology.find((t) => t?.["fields"].slug === slug))
  const filteredContentfulProducts: AllProduct = {
    productsColors: contentfulProducts.productsColors,
    products: {
      ...contentfulProducts?.products,
      items: filteredItems,
      total: filteredItems.length
    }
  }

  regionOrCity = [...(regions || []), ...(cities || [])].find((r) => r.slug === slug || r.slug2 === slug)
  if (regionOrCity) {
    return {
      props: {
        slug,
        regionOrCity,
        technology: null,
        product: null,
        legacyEntry: null,
        tags,
        contentfulProducts:
          contentfulProducts?.products?.items?.length > 0
            ? contentfulProducts
            : {
                products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
                productsColors: [] as ProductsColorsItem[]
              }
      },
      revalidate: dev ? 1 : 120
    }
  }

  const technology = await getTechnologyBySlug(slug)
  if (!!technology?.entry?.sys?.id) {
    return {
      props: {
        slug,
        regionOrCity: null,
        technology: technology?.entry,
        product: null,
        legacyEntry: null,
        tags: [],
        contentfulProducts: filteredContentfulProducts || {
          products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
          productsColors: [] as ProductsColorsItem[]
        }
      }
    }
  }

  const document = await getCourseBySlugLegacy(slug)
  if (!!document?.course?.id) {
    return {
      props: {
        slug,
        regionOrCity: null,
        technology: null,
        product: null,
        legacyEntry: document,
        tags: [],
        contentfulProducts: filteredContentfulProducts || {
          products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
          productsColors: [] as ProductsColorsItem[]
        }
      }
    }
  }

  const contentfulSlugAndId = extractSlugAndId(slug)
  const productResult = await getProductByEntryId(contentfulSlugAndId?.id)

  const product = productResult?.entry
  if (!!product?.sys?.id) {
    return {
      props: {
        slug,
        regionOrCity: null,
        technology: null,
        product: product || null,
        legacyEntry: null,
        tags: [],
        contentfulProducts: filteredContentfulProducts || {
          products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
          productsColors: [] as ProductsColorsItem[]
        }
      }
    }
  }

  return {
    notFound: true
  }
}

export default KrouzkyProDetiSlug
